import { useEffect, useRef, useState } from 'react';
import type { FieldOp } from '../utils/payloadQuery';
import { fieldLabel } from '../utils/payloadQuery';

export interface PendingPick {
  path: string;
  value: number;
  x: number;
  y: number;
}

const OPTIONS: { op: FieldOp; word: string }[] = [
  { op: 'is', word: 'is' },
  { op: 'above', word: 'is above' },
  { op: 'below', word: 'is below' },
];

const MENU_W = 190;
const MENU_H = 108;

/** Operator chooser for a clicked number. Strings and booleans skip this —
 *  they only ever mean "is", so they filter on the first click. */
export function JsonPickMenu({
  pick,
  onChoose,
  onClose,
}: {
  pick: PendingPick;
  onChoose: (op: FieldOp) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos] = useState(() => ({
    left: Math.min(pick.x, window.innerWidth - MENU_W - 8),
    top: Math.min(pick.y + 6, window.innerHeight - MENU_H - 8),
  }));

  useEffect(() => {
    ref.current?.querySelector('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* Click-away dismiss. */}
      <button
        type="button"
        aria-label="Close filter menu"
        className="fixed inset-0 z-40 cursor-default"
        onClick={onClose}
      />
      <div
        ref={ref}
        style={{ left: pos.left, top: pos.top, width: MENU_W }}
        className="fixed z-50 overflow-hidden rounded-md border border-(--color-border) bg-(--color-surface) shadow-lg"
      >
        <div className="border-b border-(--color-border) px-2.5 py-1.5 font-sans text-[0.692em] font-bold uppercase tracking-wide text-(--color-text3)">
          Filter by {fieldLabel(pick.path)}
        </div>
        {OPTIONS.map(({ op, word }) => (
          <button
            key={op}
            type="button"
            onClick={() => onChoose(op)}
            className="block w-full cursor-pointer px-2.5 py-[6px] text-left font-sans text-[0.846em] text-(--color-text) hover:bg-(--color-green-bg) hover:text-(--color-green)"
          >
            {word} <span className="font-mono font-bold">{pick.value}</span>
          </button>
        ))}
      </div>
    </>
  );
}
